import React, { useState, useMemo } from "react";
import axios from "axios";
import "./DocsUpload.scss";
import { IconBulbFilled, IconEye } from "@tabler/icons-react";
import UploadDocModal from "../../../../../components/UploadDocModal/UploadDocModal";
import FileDropzone from "../../../../../components/UploadDocModal/FileDropzone/FileDropzone";
import { useTaxOrganizerStore } from "../../../../../utils/stores/taxOrganizerStore";
import { Text, Stack, Button, Collapse, Group, Select } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { OrganizerPages } from "@constants/TaxOrganizer.ts";
import {
  useDocumentUpload,
  deleteDocument,
  type DocumentUserType,
} from "@/utils/hooks/useDocumentUpload";
import { usePresignedUrlStore } from "@/utils/stores/presignedUrlStore";
import { useAuthStore } from "@utils/stores/authStore";
import { isPdfPasswordProtected } from "@/utils/pdfPasswordUtils";
import { PdfPasswordModal } from "@/components/PdfPasswordModal/PdfPasswordModal";
import { extractApiError } from "@/utils/errors";
import { backend_url } from "@/constants";
import { AXIOS_CONFIG } from "@/utils/api";
import { Endpoints } from "@/utils/api/endpoints";
import { UploadButtonVariant } from "@/components/UploadDocModal/UploadDocModal";

export { UploadButtonVariant };

interface DocsUploadProps extends React.HTMLAttributes<HTMLDivElement> {
  taxYear: number; // REQUIRED
  showUploadTitle?: boolean;
  uploadTitleText?: string;
  onUploadComplete?: () => void;
  isMapped?: boolean;
  isSpouse?: boolean;
  onRemove?: () => void;
  compactMode?: boolean;
  clientId?: string;
  documentType?: string;
  showDocumentTypeDropdown?: boolean;
  documentTypeOptions?: { value: string; label: string }[];
  showUploadPrompt?: boolean;
  isCustomQuestion?: boolean;
  bulkUploadMode?: boolean;
  maxBulkFiles?: number;
}

const documentTypeMap: { [key: string]: string } = {
  "government issued id or social security card": "id",
};

const DocsUpload: React.FC<DocsUploadProps> = ({
  taxYear,
  showUploadTitle = true,
  isSpouse = false,
  uploadTitleText = "Upload Documents",
  isMapped = false,
  onUploadComplete,
  onRemove,
  compactMode = false,
  clientId,
  documentType,
  showDocumentTypeDropdown = false,
  documentTypeOptions = [],
  showUploadPrompt,
  isCustomQuestion = false,
  bulkUploadMode = false,
  maxBulkFiles = 50,
  ...props
}) => {
  // taxYear is now required - no fallback needed
  const {
    docsToUpload: storeDocsToUpload,
    responses,
    updateResponses,
    setIsDocumentProcessing,
    currentPage,
    isDocumentProcessing,
  } = useTaxOrganizerStore();

  const [activeDocType, setActiveDocType] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState<
    string | null
  >(null);
  const [expanded, setExpanded] = useState(false);
  const [fileInputReset, setFileInputReset] = useState(0);

  // Document viewer modal state
  const [viewerModalOpened, setViewerModalOpened] = useState(false);
  const [viewerPresignedUrl, setViewerPresignedUrl] = useState<string | null>(
    null
  );
  const [viewerDocumentName, setViewerDocumentName] = useState<string | null>(
    null
  );

  // Password handling state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [pendingUpload, setPendingUpload] = useState<{
    file: File;
    docType: string;
    userType: DocumentUserType;
    formType: string;
  } | null>(null);

  // Bulk upload state
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  );

  // Get userId from authStore or use clientId prop
  const authUserId = useAuthStore((state) => state.user?.id ?? "");
  const userId = clientId ?? authUserId;

  // Use new unified upload hook (replaces presigned URL + polling)
  const { uploadDocumentAsync } = useDocumentUpload({
    showNotifications: true,
  });

  // Use presigned URL cache store
  const { getPresignedUrl } = usePresignedUrlStore();

  // Ensure "id" exists in docsToUpload without mutating store value
  const docsToUpload = useMemo(() => {
    if (!storeDocsToUpload.hasOwnProperty("id")) {
      return { ...storeDocsToUpload, id: 1 };
    }
    return storeDocsToUpload;
  }, [storeDocsToUpload]);

  const allDocsToUpload = useMemo(() => {
    const priorityOrder = ["id", "spouse-id"];

    const compareDocuments = (a: string, b: string): number => {
      const indexA = priorityOrder.indexOf(a);
      const indexB = priorityOrder.indexOf(b);

      if (indexA !== -1 || indexB !== -1) {
        return (
          (indexA === -1 ? Infinity : indexA) -
          (indexB === -1 ? Infinity : indexB)
        );
      }

      const baseA = a.replace(/^spouse-/, "");
      const baseB = b.replace(/^spouse-/, "");

      if (baseA === baseB) {
        return a.startsWith("spouse-") ? 1 : -1;
      }

      return baseA.localeCompare(baseB);
    };

    return Object.keys(docsToUpload ?? {}).sort(compareDocuments);
  }, [docsToUpload]);

  const updateDocumentsResponse = (docType: string, file: File): void => {
    const newDoc = {
      name: file.name,
      size: file.size,
      type: file.type, // Store MIME type for preview detection
      uploadedDate: new Date().toISOString(),
    };

    updateResponses({
      ...(responses ?? {}),
      uploadedDocs: {
        ...responses?.uploadedDocs,
        [docType]: responses?.uploadedDocs?.[docType]
          ? [...(responses?.uploadedDocs?.[docType] ?? []), newDoc]
          : [newDoc],
      },
    });
  };

  const updateDocumentIdInResponses = (
    originalFileName: string,
    documentId: string
  ): void => {
    console.log("🔄 Updating document ID:", { originalFileName, documentId });

    // Get the LATEST state from the store (not the stale React variable)
    const latestResponses = useTaxOrganizerStore.getState().responses;
    console.log(
      "Current uploadedDocs before update:",
      latestResponses?.uploadedDocs
    );

    updateResponses({
      ...latestResponses,
      uploadedDocs: Object.fromEntries(
        Object.entries(latestResponses?.uploadedDocs ?? {}).map(
          ([key, value]) => [
            key,
            Array.isArray(value)
              ? value.map((doc) =>
                  doc.name === originalFileName
                    ? { ...doc, documentId } // Only store documentId, not presignedUrl
                    : doc
                )
              : value,
          ]
        )
      ),
    });
  };

  const removeFileFromResponses = async (
    fileName: string,
    documentId?: string
  ) => {
    // If we have a document ID, delete from backend first
    if (documentId) {
      try {
        await deleteDocument(documentId);
      } catch (error) {
        console.error("Failed to delete document from backend:", error);
        // Continue with local removal even if backend delete fails
      }
    }

    // Remove from local state
    updateResponses({
      ...responses,
      uploadedDocs: Object.fromEntries(
        Object.entries(responses?.uploadedDocs ?? {}).map(([key, value]) => [
          key,
          Array.isArray(value)
            ? value.filter((doc) => doc.name !== fileName)
            : value,
        ])
      ),
    });
  };

  const handleViewDocument = async (documentId: string) => {
    try {
      // Use cache store - will fetch if not cached, otherwise use cached URL
      const { presignedUrl, fileName } = await getPresignedUrl(documentId);

      // Show document in modal viewer with zoom/rotate controls
      setViewerPresignedUrl(presignedUrl);
      setViewerDocumentName(fileName);
      setViewerModalOpened(true);
    } catch (error) {
      console.error("Failed to fetch document URL:", error);
      // Could show a notification here if needed
    }
  };

  const handleUpload = async (docType: string) => {
    const file = previewFile;
    if (!file) {
      console.warn("No file to upload");
      return;
    }

    // Determine the final document type to use
    const finalDocType =
      showDocumentTypeDropdown && selectedDocumentType
        ? selectedDocumentType
        : docType;

    // Map form type
    const mappedFormType =
      documentType ??
      documentTypeMap[
        finalDocType
          .replace(/-/g, "")
          .replace(/\s+/g, " ")
          .replace("spouse", "")
          .toLowerCase()
      ] ??
      finalDocType
        .replace(/-/g, "")
        .replace(/\s+/g, " ")
        .replace("spouse", "")
        .toLowerCase();

    // Determine user type
    const userType: DocumentUserType =
      isSpouse || finalDocType.includes("spouse") ? "spouse" : "taxClient";

    // Note: Password protection is now checked earlier in updateforPreview()
    // previewFile will always be unlocked at this point

    // Store filename for error handling (before clearing state)
    const fileName = file.name;

    // Update responses immediately for UI feedback
    updateDocumentsResponse(finalDocType, file);
    setIsDocumentProcessing(true);

    try {
      // Upload using new unified hook (no polling needed!)
      // skipOcr: true for organizer uploads - OCR will run when organizer is completed
      // No password needed - file is already unlocked from password modal
      const response = await uploadDocumentAsync({
        file,
        clientId: userId,
        taxYear,
        userType,
        formType: mappedFormType,
        isCustomQuestion,
        skipOcr: true, // Skip automatic OCR for organizer uploads
      });

      // Success! Store document ID only (presignedUrl managed by cache store)
      console.log("✅ Upload succeeded:", response);

      const documentId = response?.content?.doc?.docId;

      if (documentId) {
        // Update with document ID only
        updateDocumentIdInResponses(fileName, documentId);
      } else {
        console.error("❌ Missing documentId:", { documentId });
      }

      // Clear form state on success
      setIsDocumentProcessing(false);
      setActiveDocType(null);
      setPreviewFile(null);
      setSelectedDocumentType(null);
      setFileInputReset((prev) => prev + 1); // Reset file input for next upload

      // Call completion callback
      onUploadComplete?.();
    } catch (error: any) {
      setIsDocumentProcessing(false);

      // Upload failed - remove from UI
      // The notification was already shown by the hook
      removeFileFromResponses(fileName);

      // Clear form state
      setActiveDocType(null);
      setPreviewFile(null);
      setSelectedDocumentType(null);
      setFileInputReset((prev) => prev + 1); // Reset file input for retry
    }
  };

  // Handle password modal submission
  const handlePasswordSubmit = async (password: string) => {
    if (!pendingUpload) return;

    setIsPasswordLoading(true);
    setPasswordError(null);

    // Use pendingUpload.file instead of previewFile (which may be null)
    const lockedFile = pendingUpload.file;

    try {
      // Convert file to base64
      const base64Data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result?.toString().split(",")[1] || "";
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(lockedFile);
      });

      // Validate password with backend
      const response = await axios.post(
        `${backend_url}/${Endpoints.validateFile}`,
        {
          data: base64Data,
          fileName: lockedFile.name,
          password: password,
        },
        AXIOS_CONFIG
      );

      // If validation succeeded, we have unlocked PDF
      if (response.data.content?.valid && response.data.content?.unlocked) {
        const unlockedBase64 = response.data.content.unlockedData;

        // Create a new File object from the unlocked PDF
        const unlockedBytes = Uint8Array.from(atob(unlockedBase64), (c) =>
          c.charCodeAt(0)
        );
        const unlockedBlob = new Blob([unlockedBytes], {
          type: "application/pdf",
        });
        const unlockedFile = new File([unlockedBlob], lockedFile.name, {
          type: "application/pdf",
        });

        // Close password modal
        setShowPasswordModal(false);
        setPasswordError(null);
        setIsPasswordLoading(false);

        // Show preview of UNLOCKED PDF (not the locked one!)
        setPreviewFile(unlockedFile);
        setActiveDocType(pendingUpload.docType);
        setPendingUpload(null);
      }
    } catch (error: any) {
      setIsPasswordLoading(false);
      const { type } = extractApiError(error);

      if (type === "password_incorrect") {
        // Wrong password - let user retry
        setPasswordError("Incorrect password. Please try again.");
      } else {
        // Other error - close modal and show error
        setShowPasswordModal(false);
        setPendingUpload(null);
        setPasswordError(null);
      }
    }
  };

  // Handle password modal cancellation
  const handlePasswordCancel = () => {
    setShowPasswordModal(false);
    setPendingUpload(null);
    setPasswordError(null);
    setIsDocumentProcessing(false);
    setFileInputReset((prev) => prev + 1); // Reset file input so user can retry
    // No need to remove from responses - file was never added to UI yet
  };

  // Bulk upload - upload multiple files at once
  const processBulkUpload = async (files: File[], docType: string) => {
    setIsDocumentProcessing(true);

    const validFiles: File[] = [];
    const passwordProtectedFiles: File[] = [];

    // Validate all files first
    for (const file of files) {
      // Check if PDF is password-protected
      if (file.type === "application/pdf") {
        try {
          const isProtected = await isPdfPasswordProtected(file);
          if (isProtected) {
            passwordProtectedFiles.push(file);
            continue;
          }
        } catch (error) {
          console.error("Error checking PDF password protection:", error);
        }
      }

      validFiles.push(file);
    }

    // Show notification about skipped files
    if (passwordProtectedFiles.length > 0) {
      notifications.show({
        title: "Password-Protected Files Skipped",
        message: `${passwordProtectedFiles.length} password-protected PDF${passwordProtectedFiles.length > 1 ? "s were" : " was"} skipped. Please upload ${passwordProtectedFiles.length > 1 ? "them" : "it"} individually to enter the password.`,
        color: "yellow",
        autoClose: 5000,
      });
    }

    // Upload all valid files in parallel (with limit of 5 concurrent uploads)
    const results = await uploadFilesInBatches(validFiles, docType, 5);

    // Show completion summary
    const successful = results.filter((r) => r.success).length;
    const failed = results.filter((r) => !r.success).length;

    notifications.show({
      title: "Bulk Upload Complete",
      message: `✓ ${successful} file${successful !== 1 ? "s" : ""} uploaded successfully${failed > 0 ? `, ✗ ${failed} failed` : ""}. Click any file to preview.`,
      color: successful > 0 ? "green" : "red",
      autoClose: 5000,
    });

    setIsDocumentProcessing(false);
    onUploadComplete?.();
  };

  // Upload files in batches to avoid overwhelming browser/backend
  const uploadFilesInBatches = async (
    files: File[],
    docType: string,
    concurrency: number
  ): Promise<Array<{ file: File; success: boolean; error?: string }>> => {
    const results: Array<{ file: File; success: boolean; error?: string }> = [];
    const uploadedFiles: Array<{ file: File; documentId?: string }> = [];

    // Determine the final document type for all files
    const finalDocType =
      showDocumentTypeDropdown && selectedDocumentType
        ? selectedDocumentType
        : docType;

    // Split into batches
    for (let i = 0; i < files.length; i += concurrency) {
      const batch = files.slice(i, i + concurrency);

      // Upload batch in parallel
      const batchResults = await Promise.allSettled(
        batch.map((file) => uploadSingleFile(file, docType))
      );

      // Process results
      batchResults.forEach((result, idx) => {
        const file = batch[idx];
        if (result.status === "fulfilled") {
          results.push({ file, success: true });
          uploadedFiles.push(result.value);
        } else {
          results.push({ file, success: false, error: String(result.reason) });
        }
      });
    }

    // Batch update state with ALL uploaded files at once
    if (uploadedFiles.length > 0) {
      // Get current state
      const currentDocs = responses?.uploadedDocs?.[finalDocType] ?? [];

      // Create new documents array (presignedUrl managed by cache store)
      const newDocs = uploadedFiles.map(({ file, documentId }) => ({
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedDate: new Date().toISOString(),
        documentId,
      }));

      // Single state update with all files
      updateResponses({
        ...(responses ?? {}),
        uploadedDocs: {
          ...responses?.uploadedDocs,
          [finalDocType]: [...currentDocs, ...newDocs],
        },
      });
    }

    return results;
  };

  // Upload a single file (used by bulk upload)
  const uploadSingleFile = async (
    file: File,
    docType: string
  ): Promise<{ file: File; documentId?: string }> => {
    // Determine the final document type
    const finalDocType =
      showDocumentTypeDropdown && selectedDocumentType
        ? selectedDocumentType
        : docType;

    // Map form type
    const mappedFormType =
      documentType ??
      documentTypeMap[
        finalDocType
          .replace(/-/g, "")
          .replace(/\s+/g, " ")
          .replace("spouse", "")
          .toLowerCase()
      ] ??
      finalDocType
        .replace(/-/g, "")
        .replace(/\s+/g, " ")
        .replace("spouse", "")
        .toLowerCase();

    // Determine user type
    const userType: DocumentUserType =
      isSpouse || finalDocType.includes("spouse") ? "spouse" : "taxClient";

    // Update progress to 25% (started)
    setUploadProgress((prev) => ({ ...prev, [file.name]: 25 }));

    try {
      // Upload using hook
      const response = await uploadDocumentAsync({
        file,
        clientId: userId,
        taxYear,
        userType,
        formType: mappedFormType,
        isCustomQuestion,
        skipOcr: true,
      });

      // Update progress to 100% (complete)
      setUploadProgress((prev) => ({ ...prev, [file.name]: 100 }));

      // Get document ID only (presignedUrl managed by cache store)
      const documentId = response?.content?.doc?.docId;

      // Return file info for batch update
      return { file, documentId };
    } catch (error) {
      console.error("Upload failed:", file.name, error);
      throw error;
    }
  };

  const updateforPreview = async (files: File[], docType: string) => {
    // Bulk upload mode - skip preview, upload directly
    if (bulkUploadMode && files.length > 1) {
      await processBulkUpload(files, docType);
      return;
    }

    // Single file mode - existing preview logic
    if (files.length > 0) {
      const file = files[0];

      // Check if PDF is password-protected BEFORE showing preview
      if (file.type === "application/pdf") {
        try {
          const isProtected = await isPdfPasswordProtected(file);
          if (isProtected) {
            // Determine form type for backend
            const finalDocType =
              showDocumentTypeDropdown && selectedDocumentType
                ? selectedDocumentType
                : docType;

            const mappedFormType =
              documentType ??
              documentTypeMap[
                finalDocType
                  .replace(/-/g, "")
                  .replace(/\s+/g, " ")
                  .replace("spouse", "")
                  .toLowerCase()
              ] ??
              finalDocType
                .replace(/-/g, "")
                .replace(/\s+/g, " ")
                .replace("spouse", "")
                .toLowerCase();

            const userType: DocumentUserType =
              isSpouse || finalDocType.includes("spouse")
                ? "spouse"
                : "taxClient";

            // Store pending upload info and show password modal (no preview yet)
            setPendingUpload({
              file,
              docType: finalDocType,
              userType,
              formType: mappedFormType,
            });
            setShowPasswordModal(true);
            return; // Don't show preview modal yet
          }
        } catch (error) {
          console.error("Error checking PDF password protection:", error);
          // Continue with preview anyway if check fails
        }
      }

      // File is not password-protected (or not a PDF), show preview
      setPreviewFile(file);
      setActiveDocType(docType);
    }
  };

  const renderUploadContainer = (docType: any) => {
    const count = docsToUpload?.[docType];

    let showUploadArea = true;
    if (docType === "id" || docType === "spouse-id") {
      showUploadArea =
        Object.keys(responses?.uploadedDocs?.[docType] ?? []).length === 0;
    }

    let hasSpouse = false;
    const filingStatus = responses?.personalInfo?.[0]?.filingStatus;
    if (filingStatus) {
      hasSpouse = filingStatus.toLowerCase().includes("married");
    }

    const documentTitleMap: { [key: string]: string } = {
      id: "Government-issued ID",
      "spouse-id": "Spouse's Government-issued ID",
      w2: "W-2",
      "spouse-w2": "Spouse's W-2",
      "1099": "1099",
      "spouse-1099": "Spouse's 1099",
    };

    let documentTitle = documentTitleMap[docType] || docType;

    if (docType.startsWith("spouse-") && !documentTitleMap[docType]) {
      const baseDocType = docType.replace("spouse-", "");
      documentTitle = `Spouse's ${documentTitleMap[baseDocType] || baseDocType}`;
    }

    if (compactMode) {
      return (
        <div key={docType} className="compact-upload-container">
          {showDocumentTypeDropdown && (
            <Select
              data={documentTypeOptions}
              placeholder="Select document type"
              value={selectedDocumentType}
              onChange={setSelectedDocumentType}
              required
              mb="sm"
              disabled={isDocumentProcessing}
            />
          )}
          <FileDropzone
            fileDetails={responses?.uploadedDocs?.[docType] ?? []}
            allowUpload={showUploadArea}
            disabled={
              currentPage === OrganizerPages.REVIEW ||
              isDocumentProcessing ||
              (showDocumentTypeDropdown && !selectedDocumentType)
            }
            organizer={true}
            compactMode={true}
            setFile={(files) => updateforPreview(files, docType)}
            onCancel={(fileName, documentId) => {
              removeFileFromResponses(fileName, documentId);
              onRemove?.();
            }}
            onViewDocument={handleViewDocument}
            bulkMode={bulkUploadMode}
            maxBulkFiles={maxBulkFiles}
            uploadProgress={uploadProgress}
            resetTrigger={fileInputReset}
          />
        </div>
      );
    }

    return (
      <Stack className="upload-container" key={docType}>
        {showUploadTitle && (
          <Text size="lg">
            {documentTitle}
            {count > 1 ? ` (${count} required)` : ""}
          </Text>
        )}
        {showDocumentTypeDropdown && (
          <Select
            data={documentTypeOptions}
            placeholder="Select document type"
            value={selectedDocumentType}
            onChange={setSelectedDocumentType}
            required
            disabled={isDocumentProcessing}
          />
        )}
        <FileDropzone
          fileDetails={responses?.uploadedDocs?.[docType] ?? []}
          allowUpload={showUploadArea}
          disabled={
            currentPage === OrganizerPages.REVIEW ||
            isDocumentProcessing ||
            (showDocumentTypeDropdown && !selectedDocumentType)
          }
          organizer={true}
          setFile={(files) => updateforPreview(files, docType)}
          onCancel={(fileName, documentId) => {
            removeFileFromResponses(fileName, documentId);
            onRemove?.();
          }}
          onViewDocument={handleViewDocument}
          bulkMode={bulkUploadMode}
          maxBulkFiles={maxBulkFiles}
          uploadProgress={uploadProgress}
          resetTrigger={fileInputReset}
        />
        {count > 1 && (
          <div className="upload-prompt">
            <IconBulbFilled color="#0077CC" size={"2rem"} />
            {`Upload ${count > 1 && "all"} the ${docType} form${
              count > 1 ? "s" : ""
            } you ${
              hasSpouse ? "and your spouse have" : "have"
            } for this year.`}
          </div>
        )}
      </Stack>
    );
  };

  if (compactMode) {
    return (
      <>
        <Group justify="space-between" mb="sm">
          <Text size="md" fw={500}>
            Quick Upload
          </Text>
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconEye size={14} />}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "Hide" : "Show"}
          </Button>
        </Group>

        <Collapse in={expanded}>
          <Stack gap={8}>
            {isMapped
              ? allDocsToUpload.map((doc) => renderUploadContainer(doc))
              : renderUploadContainer(uploadTitleText)}
          </Stack>
        </Collapse>

        <UploadDocModal
          modalOpened={!!activeDocType}
          setModalOpened={(isOpened) => {
            if (!isOpened) {
              setActiveDocType(null);
              setPreviewFile(null);
              setIsDocumentProcessing(false);
              setFileInputReset((prev) => prev + 1); // Reset file input
            }
          }}
          onDocumentUploaded={() =>
            activeDocType && handleUpload(activeDocType)
          }
          uploadButtonVariant={UploadButtonVariant.SECONDARY}
          onUploadClick={
            activeDocType ? () => handleUpload(activeDocType) : undefined
          }
          previewFile={previewFile}
        />
      </>
    );
  }

  return (
    <Stack {...props}>
      {isMapped
        ? allDocsToUpload.map((doc) => renderUploadContainer(doc))
        : renderUploadContainer(uploadTitleText)}
      <UploadDocModal
        modalOpened={!!activeDocType}
        setModalOpened={(isOpened) => {
          if (!isOpened) {
            setActiveDocType(null);
            setPreviewFile(null);
            setIsDocumentProcessing(false);
            setFileInputReset((prev) => prev + 1); // Reset file input
          }
        }}
        onDocumentUploaded={() => activeDocType && handleUpload(activeDocType)}
        uploadButtonVariant={UploadButtonVariant.SECONDARY}
        onUploadClick={
          activeDocType ? () => handleUpload(activeDocType) : undefined
        }
        previewFile={previewFile}
      />

      {/* Password prompt modal for encrypted PDFs */}
      <PdfPasswordModal
        opened={showPasswordModal}
        fileName={pendingUpload?.file.name || ""}
        onSubmit={handlePasswordSubmit}
        onCancel={handlePasswordCancel}
        isLoading={isPasswordLoading}
        error={passwordError}
      />

      {/* Document viewer modal with zoom/rotate controls */}
      <UploadDocModal
        modalOpened={viewerModalOpened}
        setModalOpened={setViewerModalOpened}
        onDocumentUploaded={() => {}} // Not used in view-only mode
        uploadButtonVariant={UploadButtonVariant.SECONDARY}
        viewOnly={true}
        presignedUrl={viewerPresignedUrl}
        documentName={viewerDocumentName}
      />
    </Stack>
  );
};

export default DocsUpload;
